import { useState, type FormEvent } from 'react'
import { del, post, put } from '../../../api'
import { Alert, Button, Card, Field, Input, Modal, Select } from '../../../components/ui'
import { useAction } from '../../../hooks'
import { WEEKDAYS, daysLabel, longDate, todayKey } from '../../../format'
import type { ServicePeriod, SpecialDate } from '../../../types'
import { useBusiness } from '../Layout'

// Regular service periods (lunch, dinner) and the exceptions: closed days and one-off hours.

type PeriodForm = { name: string; daysOfWeek: number[]; starts: string; ends: string; slotIntervalMin: number; pacingCoversPerInterval: string; effectiveFrom: string; effectiveTo: string }

function toForm(p?: ServicePeriod): PeriodForm {
  return p
    ? { name: p.name, daysOfWeek: p.daysOfWeek, starts: p.starts, ends: p.ends, slotIntervalMin: p.slotIntervalMin, pacingCoversPerInterval: p.pacingCoversPerInterval?.toString() ?? '', effectiveFrom: p.effectiveFrom, effectiveTo: p.effectiveTo ?? '' }
    : { name: 'Dinner', daysOfWeek: [1, 2, 3, 4, 5, 6, 0], starts: '18:00', ends: '22:30', slotIntervalMin: 15, pacingCoversPerInterval: '', effectiveFrom: '2020-01-01', effectiveTo: '' }
}

export function HoursSection() {
  const { business, reload, can } = useBusiness()
  const [editing, setEditing] = useState<ServicePeriod | 'new' | null>(null)
  const [addingDate, setAddingDate] = useState(false)
  const remove = useAction(async (p: ServicePeriod) => {
    if (!confirm(`Remove ${p.name}? Existing bookings are kept; no new bookings will be offered in these hours.`)) return
    await del(`/v1/admin/businesses/${business.id}/service-periods/${p.id}`)
    await reload({ quiet: true })
  })
  const removeDate = useAction(async (d: SpecialDate) => {
    await del(`/v1/admin/businesses/${business.id}/special-dates/${d.date}`)
    await reload({ quiet: true })
  })
  const editable = can('manager')
  const today = todayKey(business.timezone)
  const upcoming = business.specialDates.filter((d) => d.date >= today).sort((a, b) => a.date.localeCompare(b.date))

  return (
    <div className="stack-lg">
      <Card
        title="Service hours"
        actions={
          editable ? (
            <Button size="sm" onClick={() => setEditing('new')}>
              + Add service
            </Button>
          ) : null
        }
      >
        {remove.error ? <Alert>{remove.error}</Alert> : null}
        <ul className="list-plain">
          {business.servicePeriods.map((p) => (
            <li key={p.id}>
              <div>
                <div style={{ fontWeight: 600 }}>
                  {p.name} · {p.starts}–{p.ends}
                </div>
                <div className="muted small">
                  {daysLabel(p.daysOfWeek)} · every {p.slotIntervalMin} min{p.pacingCoversPerInterval ? ` · max ${p.pacingCoversPerInterval} covers per slot` : ''}
                  {p.effectiveTo ? ` · until ${p.effectiveTo}` : ''}
                </div>
              </div>
              {editable ? (
                <div className="row-flex">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(p)}>
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => void remove.run(p)} disabled={business.servicePeriods.length === 1}>
                    Remove
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
        <p className="hint">Times are when bookings open and when the kitchen closes. Guests are offered times that finish by closing (90 minutes for two, up to 150 for large groups) unless late seatings are allowed in Booking rules; staff can seat later.</p>
      </Card>

      <Card
        title="Closures and special hours"
        actions={
          editable ? (
            <Button size="sm" onClick={() => setAddingDate(true)}>
              + Add a date
            </Button>
          ) : null
        }
      >
        {removeDate.error ? <Alert>{removeDate.error}</Alert> : null}
        {upcoming.length === 0 ? <p className="muted small">No closures or special hours coming up.</p> : null}
        <ul className="list-plain">
          {upcoming.map((d) => (
            <li key={d.id}>
              <div>
                <div style={{ fontWeight: 600 }}>{longDate(d.date)}</div>
                <div className="muted small">{d.kind === 'closed' ? 'Closed' : `Open ${d.starts}–${d.ends}`}{d.label ? ` · ${d.label}` : ''}</div>
              </div>
              {editable ? (
                <Button size="sm" variant="ghost" onClick={() => void removeDate.run(d)}>
                  Remove
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      </Card>

      {editing ? <PeriodModal period={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} onSaved={() => (setEditing(null), void reload({ quiet: true }))} /> : null}
      {addingDate ? <SpecialDateModal onClose={() => setAddingDate(false)} onSaved={() => (setAddingDate(false), void reload({ quiet: true }))} /> : null}
    </div>
  )
}

function PeriodModal({ period, onClose, onSaved }: { period?: ServicePeriod; onClose: () => void; onSaved: () => void }) {
  const { business } = useBusiness()
  const [f, setF] = useState<PeriodForm>(toForm(period))
  const save = useAction(async () => {
    const body = { ...f, pacingCoversPerInterval: f.pacingCoversPerInterval ? Number(f.pacingCoversPerInterval) : null, effectiveTo: f.effectiveTo || null }
    if (period) await put(`/v1/admin/businesses/${business.id}/service-periods/${period.id}`, body)
    else await post(`/v1/admin/businesses/${business.id}/service-periods`, body)
    onSaved()
  })
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (f.daysOfWeek.length === 0) return
    void save.run()
  }
  return (
    <Modal
      title={period ? `Edit ${period.name}` : 'Add a service'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="period-form" busy={save.busy}>
            Save
          </Button>
        </>
      }
    >
      <form id="period-form" onSubmit={submit} className="stack">
        {save.error ? <Alert>{save.error}</Alert> : null}
        <Field label="Name">{(id) => <Input id={id} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required />}</Field>
        <div className="checks" role="group" aria-label="Days">
          {[1, 2, 3, 4, 5, 6, 0].map((d) => (
            <label key={d}>
              <input type="checkbox" checked={f.daysOfWeek.includes(d)} onChange={(e) => setF({ ...f, daysOfWeek: e.target.checked ? [...f.daysOfWeek, d] : f.daysOfWeek.filter((x) => x !== d) })} />
              {WEEKDAYS[d]}
            </label>
          ))}
        </div>
        <div className="inline-fields">
          <Field label="Opens">{(id) => <Input id={id} type="time" value={f.starts} onChange={(e) => setF({ ...f, starts: e.target.value })} step={900} required />}</Field>
          <Field label="Kitchen closes">{(id) => <Input id={id} type="time" value={f.ends} onChange={(e) => setF({ ...f, ends: e.target.value })} step={900} required />}</Field>
          <Field label="Booking times every">
            {(id) => (
              <Select id={id} value={f.slotIntervalMin} onChange={(e) => setF({ ...f, slotIntervalMin: Number(e.target.value) })}>
                {[15, 30, 45, 60].map((m) => (
                  <option key={m} value={m}>
                    {m} min
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
        <Field label="Max new covers per booking time (kitchen pacing)" hint="Leave blank for no limit beyond the room capacity.">
          {(id) => <Input id={id} type="number" min={1} value={f.pacingCoversPerInterval} onChange={(e) => setF({ ...f, pacingCoversPerInterval: e.target.value })} />}
        </Field>
        <div className="grid-2">
          <Field label="From">{(id) => <Input id={id} type="date" value={f.effectiveFrom} onChange={(e) => setF({ ...f, effectiveFrom: e.target.value })} required />}</Field>
          <Field label="Until (optional)">{(id) => <Input id={id} type="date" value={f.effectiveTo} onChange={(e) => setF({ ...f, effectiveTo: e.target.value })} />}</Field>
        </div>
      </form>
    </Modal>
  )
}

function SpecialDateModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { business } = useBusiness()
  const [date, setDate] = useState('')
  const [kind, setKind] = useState<'closed' | 'override'>('closed')
  const [label, setLabel] = useState('')
  const [starts, setStarts] = useState('12:00')
  const [ends, setEnds] = useState('22:00')
  const [affected, setAffected] = useState<{ reference: string; startsAt: string; partySize: number }[] | null>(null)
  const save = useAction(async () => {
    const r = await put<{ affectedBookings: { reference: string; startsAt: string; partySize: number }[] }>(`/v1/admin/businesses/${business.id}/special-dates`, { date, kind, label: label || undefined, ...(kind === 'override' ? { starts, ends } : {}) })
    if (r.affectedBookings.length) setAffected(r.affectedBookings)
    else onSaved()
  })
  return (
    <Modal
      title="Closure or special hours"
      onClose={onClose}
      footer={
        affected ? (
          <Button variant="primary" onClick={onSaved}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" form="special-date-form" busy={save.busy}>
              Save
            </Button>
          </>
        )
      }
    >
      {affected ? (
        <div className="stack">
          <Alert kind="info">Saved. {affected.length} existing {affected.length === 1 ? 'booking is' : 'bookings are'} on this day; please contact these guests.</Alert>
          <ul className="list-plain small">
            {affected.map((b) => (
              <li key={b.reference}>
                <span className="mono">{b.reference}</span>
                <span>
                  {new Date(b.startsAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: business.timezone })} · {b.partySize} guests
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <form
          id="special-date-form"
          className="stack"
          onSubmit={(e) => {
            e.preventDefault()
            void save.run()
          }}
        >
          {save.error ? <Alert>{save.error}</Alert> : null}
          <Field label="Date">{(id) => <Input id={id} type="date" value={date} min={todayKey(business.timezone)} onChange={(e) => setDate(e.target.value)} required />}</Field>
          <Field label="What happens">
            {(id) => (
              <Select id={id} value={kind} onChange={(e) => setKind(e.target.value as 'closed' | 'override')}>
                <option value="closed">Closed all day</option>
                <option value="override">Different hours</option>
              </Select>
            )}
          </Field>
          {kind === 'override' ? (
            <div className="grid-2">
              <Field label="Opens">{(id) => <Input id={id} type="time" value={starts} onChange={(e) => setStarts(e.target.value)} step={900} required />}</Field>
              <Field label="Kitchen closes">{(id) => <Input id={id} type="time" value={ends} onChange={(e) => setEnds(e.target.value)} step={900} required />}</Field>
            </div>
          ) : null}
          <Field label="Label (optional)" hint="For your own reference, e.g. Private event, Bank holiday.">{(id) => <Input id={id} value={label} onChange={(e) => setLabel(e.target.value)} />}</Field>
        </form>
      )}
    </Modal>
  )
}
